import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  FaUserFriends,
  FaCalendarCheck,
  FaFileAlt,
  FaChartLine,
  FaLock,
  FaMobile,
  FaCheckCircle,
  FaStar,
  FaServer
} from 'react-icons/fa'

const HomePage = () => {
  const features = [
    {
      icon: <FaUserFriends className="w-8 h-8" />,
      title: 'Client Management',
      description: 'Organize all client information, track progress, and maintain detailed records in one secure location.',
      bgColor: 'bg-blue-100',
      iconColor: 'text-blue-600'
    },
    {
      icon: <FaFileAlt className="w-8 h-8" />,
      title: 'Session Notes',
      description: 'Create customizable session notes with templates, structured assessments, and secure storage.',
      bgColor: 'bg-orange-100',
      iconColor: 'text-orange-600'
    },
    {
      icon: <FaCalendarCheck className="w-8 h-8" />,
      title: 'Scheduling',
      description: 'Manage appointments with calendar sync, automated reminders, and recurring sessions.',
      bgColor: 'bg-teal-100',
      iconColor: 'text-teal-600'
    },
    {
      icon: <FaChartLine className="w-8 h-8" />,
      title: 'Analytics',
      description: 'Track practice growth, revenue, and client outcomes with comprehensive dashboards.',
      bgColor: 'bg-green-100',
      iconColor: 'text-green-600'
    },
    {
      icon: <FaLock className="w-8 h-8" />,
      title: 'HIPAA Compliant',
      description: 'Enterprise-grade security with encryption, audit logs, and compliance features built-in.',
      bgColor: 'bg-purple-100',
      iconColor: 'text-purple-600'
    },
    {
      icon: <FaMobile className="w-8 h-8" />,
      title: 'Mobile Ready',
      description: 'Access your practice from anywhere with our responsive web app and offline support.',
      bgColor: 'bg-pink-100',
      iconColor: 'text-pink-600'
    }
  ]

  const testimonials = [
    {
      name: 'Dr. Sarah Martinez',
      role: 'Licensed Therapist',
      avatar: 'https://ui-avatars.com/api/?name=Sarah+Martinez&background=random',
      content: 'Acuity Practice has transformed how I manage my practice. The session notes feature alone has saved me hours every week.',
      rating: 5
    },
    {
      name: 'James Chen',
      role: 'Nutritionist & Health Coach',
      avatar: 'https://ui-avatars.com/api/?name=James+Chen&background=random',
      content: 'The scheduling and billing features are incredibly intuitive. My clients love the convenience of the client portal.',
      rating: 5
    },
    {
      name: 'Emily Rodriguez',
      role: 'Wellness Center Director',
      avatar: 'https://ui-avatars.com/api/?name=Emily+Rodriguez&background=random',
      content: 'Managing our team of 8 practitioners has never been easier. The multi-tenant features are exactly what we needed.',
      rating: 5
    }
  ]

  const stats = [
    { icon: <FaUserFriends className="w-8 h-8" />, number: 'Trusted by', label: 'Practitioners Worldwide' },
    { icon: <FaCheckCircle className="w-8 h-8" />, number: 'Built for', label: 'Multi-Specialty Wellness' },
    { icon: <FaServer className="w-8 h-8" />, number: '99.9%', label: 'Uptime' },
    { icon: <FaStar className="w-8 h-8" />, number: '30-day', label: 'Free Trial' }
  ]

  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="gradient-bg section-padding">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 mb-6 leading-tight tracking-tight">
                Bringing <span className="text-primary-600">Clarity</span> to Your Practice
              </h1>
              <p className="text-lg md:text-2xl text-gray-700 mb-8 leading-relaxed max-w-2xl">
                The all-in-one platform for wellness practitioners to manage clients,
                sessions, scheduling, and billing—all in one beautiful, secure space.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 mb-6">
                <a href="https://acuitypractice.com/signup?plan=flourish&trial=true" className="btn-primary text-center text-lg font-semibold px-8 py-4">
                  Start Free Trial
                </a>
                <Link to="/pricing" className="btn-secondary text-center text-lg font-semibold px-8 py-4">
                  See All Plans
                </Link>
              </div>
              <p className="text-sm text-gray-600 font-medium">
                ✨ 30-day free trial • No credit card required • Cancel anytime
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              {/* Dashboard Screenshot Container */}
              <div className="relative z-10">
                {/* Product Dashboard Image */}
                <div className="bg-white rounded-3xl shadow-2xl p-1 overflow-hidden border border-gray-100">
                  <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 space-y-6">
                    {/* Header */}
                    <div className="flex items-center justify-between pb-4 border-b border-gray-100">
                      <div>
                        <p className="text-sm font-semibold text-gray-600">Dashboard</p>
                        <h3 className="text-xl font-bold text-gray-900">Today's Summary</h3>
                      </div>
                      <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                        <div className="w-6 h-6 bg-primary-500 rounded"></div>
                      </div>
                    </div>

                    {/* Key Metrics Grid */}
                    <div className="grid grid-cols-3 gap-4">
                      <div className="bg-white rounded-lg p-4 border border-gray-100">
                        <p className="text-xs text-gray-600 font-medium mb-2">Active Clients</p>
                        <p className="text-2xl font-bold text-gray-900">42</p>
                        <p className="text-xs text-green-600 font-medium mt-1">↑ 3 this week</p>
                      </div>
                      <div className="bg-white rounded-lg p-4 border border-gray-100">
                        <p className="text-xs text-gray-600 font-medium mb-2">Today Revenue</p>
                        <p className="text-2xl font-bold text-green-600">$840</p>
                        <p className="text-xs text-gray-600 font-medium mt-1">2 appointments</p>
                      </div>
                      <div className="bg-white rounded-lg p-4 border border-gray-100">
                        <p className="text-xs text-gray-600 font-medium mb-2">Next Session</p>
                        <p className="text-lg font-bold text-gray-900">3:00 PM</p>
                        <p className="text-xs text-gray-600 font-medium mt-1">Sarah M.</p>
                      </div>
                    </div>

                    {/* Recent Activity */}
                    <div className="space-y-3">
                      <p className="text-xs font-semibold text-gray-600 uppercase">Recent Activity</p>
                      <div className="space-y-2">
                        <div className="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-100">
                          <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <FaCheckCircle className="text-green-600 w-4 h-4" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900">Session completed</p>
                            <p className="text-xs text-gray-500">James Chen • CBT Session</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-100">
                          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <FaCalendarCheck className="text-blue-600 w-4 h-4" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900">Appointment booked</p>
                            <p className="text-xs text-gray-500">Maria Garcia • Tomorrow 2:00 PM</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Floating Notification Badge */}
                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.8, duration: 0.4 }}
                  className="absolute -top-4 -right-4 bg-gradient-to-r from-primary-600 to-primary-700 text-white px-4 py-3 rounded-full shadow-lg flex items-center gap-2 font-medium text-sm"
                >
                  <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                  Live sync enabled
                </motion.div>
              </div>

              {/* Background Blur Effects */}
              <div className="absolute -bottom-8 -right-8 w-72 h-72 bg-primary-200 rounded-full blur-3xl opacity-20"></div>
              <div className="absolute -top-8 -left-8 w-72 h-72 bg-accent-200 rounded-full blur-3xl opacity-20"></div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-white section-padding border-t-4 border-success-200">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12 lg:gap-16">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="text-primary-600 text-3xl md:text-4xl mb-4 flex justify-center">
                  {stat.icon}
                </div>
                <div className="text-3xl md:text-4xl font-bold text-primary-600 mb-2">
                  {stat.number}
                </div>
                <div className="text-gray-600 font-semibold text-sm">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section - Dark Green with Vibrant Cards */}
      <section className="section-padding bg-gradient-to-b from-emerald-900 via-emerald-800 to-emerald-900 relative overflow-hidden">
        {/* Background accent circles */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-emerald-600 rounded-full blur-3xl opacity-10"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-emerald-500 rounded-full blur-3xl opacity-10"></div>

        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight tracking-tight">
              Everything You Need to Thrive
            </h2>
            <p className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Built specifically for therapists, coaches, nutritionists, and holistic healers
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12">
            {features.map((feature, index) => {
              const cardColors = [
                { bg: 'bg-emerald-500', border: 'border-emerald-300', shadow: 'shadow-emerald-500/30' },
                { bg: 'bg-cyan-500', border: 'border-cyan-300', shadow: 'shadow-cyan-500/30' },
                { bg: 'bg-blue-500', border: 'border-blue-300', shadow: 'shadow-blue-500/30' },
                { bg: 'bg-emerald-600', border: 'border-emerald-400', shadow: 'shadow-emerald-600/30' },
                { bg: 'bg-teal-500', border: 'border-teal-300', shadow: 'shadow-teal-500/30' },
                { bg: 'bg-green-600', border: 'border-green-400', shadow: 'shadow-green-600/30' }
              ];
              const colors = cardColors[index % cardColors.length];

              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className={`${colors.bg} rounded-2xl p-8 border-2 ${colors.border} shadow-2xl ${colors.shadow} h-full hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 group`}
                >
                  <div className="w-16 h-16 rounded-xl bg-white/10 flex items-center justify-center mb-6 group-hover:bg-white/20 transition-colors duration-300">
                    <div className="text-4xl text-white">
                      {feature.icon}
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-4">
                    {feature.title}
                  </h3>
                  <p className="text-gray-100 leading-relaxed text-base">
                    {feature.description}
                  </p>
                </motion.div>
              );
            })}
          </div>

          {/* Admin Dashboard Preview */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="mt-20"
          >
            <div className="text-center mb-12">
              <h3 className="text-3xl md:text-4xl font-bold text-white mb-4">See It In Action</h3>
              <p className="text-lg text-gray-300">Beautiful, intuitive admin interface designed for wellness practitioners</p>
            </div>

            <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border-4 border-white/20">
              <div className="bg-gradient-to-br from-gray-100 to-gray-50 p-1">
                <div className="bg-white rounded-2xl p-8 md:p-12">
                  {/* Browser Header */}
                  <div className="mb-6 pb-4 border-b border-gray-200">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="flex gap-2">
                        <div className="w-3 h-3 rounded-full bg-red-500"></div>
                        <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                        <div className="w-3 h-3 rounded-full bg-green-500"></div>
                      </div>
                      <div className="flex-1 text-center text-sm text-gray-500">acuitypractice.com/dashboard</div>
                    </div>
                  </div>

                  {/* Mock Dashboard Content */}
                  <div className="space-y-6">
                    {/* Header */}
                    <div className="flex items-center justify-between pb-4 border-b border-gray-100">
                      <div>
                        <h4 className="text-xl font-bold text-gray-900">Dashboard</h4>
                        <p className="text-sm text-gray-600 mt-1">Welcome back to your practice</p>
                      </div>
                      <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
                        <div className="w-8 h-8 bg-emerald-500 rounded"></div>
                      </div>
                    </div>

                    {/* Stats Grid */}
                    <div className="grid grid-cols-3 gap-4">
                      <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-lg p-4 border border-emerald-100">
                        <p className="text-xs text-gray-600 font-medium mb-2">Active Clients</p>
                        <p className="text-2xl font-bold text-gray-900">42</p>
                        <p className="text-xs text-emerald-600 font-medium mt-2">↑ 3 this week</p>
                      </div>
                      <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-lg p-4 border border-blue-100">
                        <p className="text-xs text-gray-600 font-medium mb-2">Today Revenue</p>
                        <p className="text-2xl font-bold text-gray-900">$1,240</p>
                        <p className="text-xs text-blue-600 font-medium mt-2">4 sessions</p>
                      </div>
                      <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg p-4 border border-purple-100">
                        <p className="text-xs text-gray-600 font-medium mb-2">Next Appointment</p>
                        <p className="text-sm font-bold text-gray-900">2:00 PM</p>
                        <p className="text-xs text-purple-600 font-medium mt-2">Sarah M.</p>
                      </div>
                    </div>

                    {/* Activity List */}
                    <div className="space-y-3 mt-6">
                      <p className="text-xs font-semibold text-gray-600 uppercase">Recent Activity</p>
                      <div className="space-y-2">
                        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-100">
                          <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <span className="text-emerald-600 font-bold text-xs">✓</span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900">Session completed</p>
                            <p className="text-xs text-gray-500">James Chen • CBT Session</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-100">
                          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <span className="text-blue-600 font-bold text-xs">📅</span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900">Appointment booked</p>
                            <p className="text-xs text-gray-500">Maria Garcia • Tomorrow 3:30 PM</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </motion.div>

          <div className="text-center mt-16">
            <Link to="/features" className="btn-primary text-lg bg-white text-primary-600 hover:bg-gray-100">
              View All Features
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials Section - Light with Accent - HIDDEN FOR NOW */}
      <section className="hidden section-padding bg-gradient-to-b from-white via-gray-50 to-white border-t-8 border-accent-500">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight tracking-tight">
              Loved by Practitioners Everywhere
            </h2>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Join thousands of happy practitioners transforming their practices
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl shadow-lg p-8 border-2 border-accent-100 h-full flex flex-col hover:shadow-2xl hover:border-accent-300 transition-all duration-300 hover:-translate-y-1"
              >
                {/* Stars at top */}
                <div className="flex mb-6">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <FaStar key={i} className="text-yellow-400 w-5 h-5" />
                  ))}
                </div>

                {/* Quote */}
                <p className="text-gray-700 italic leading-relaxed text-base flex-grow mb-6">
                  "{testimonial.content}"
                </p>

                {/* Divider */}
                <div className="h-px bg-gradient-to-r from-transparent via-accent-300 to-transparent mb-6"></div>

                {/* Avatar and Name/Role at bottom */}
                <div className="flex items-center gap-4">
                  <img
                    src={testimonial.avatar}
                    alt={testimonial.name}
                    className="w-14 h-14 rounded-full object-cover border-2 border-accent-200"
                  />
                  <div>
                    <p className="font-bold text-sm text-gray-900">{testimonial.name}</p>
                    <p className="text-xs text-accent-600 font-semibold">{testimonial.role}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section - Bold Dark Background */}
      <section className="section-padding bg-gradient-to-r from-primary-900 via-primary-800 to-primary-900 relative overflow-hidden">
        {/* Background accent circles */}
        <div className="absolute top-0 left-0 w-72 h-72 bg-primary-600 rounded-full blur-3xl opacity-20"></div>
        <div className="absolute bottom-0 right-0 w-72 h-72 bg-accent-600 rounded-full blur-3xl opacity-20"></div>

        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-white to-gray-50 rounded-3xl shadow-2xl p-12 md:p-16 text-center border-2 border-white/20"
          >
            <h2 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight tracking-tight">
              Ready to Transform Your Practice?
            </h2>
            <p className="text-lg md:text-xl text-gray-600 mb-10 max-w-2xl mx-auto leading-relaxed">
              Join thousands of practitioners who have simplified their workflow and
              grown their practice with Acuity Practice.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <a href="https://acuitypractice.com/signup?plan=flourish&trial=true" className="btn-primary text-lg px-10 py-4 hover:scale-105 transition-transform duration-300">
                Start Free 14-Day Trial
              </a>
              <Link to="/pricing" className="btn-secondary text-lg px-10 py-4 hover:scale-105 transition-transform duration-300">
                View Pricing
              </Link>
            </div>
            <p className="text-base text-gray-600 mt-10 font-medium">
              ✓ No credit card required • ✓ Free 30-day trial • ✓ Cancel anytime
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  )
}

export default HomePage
